import { State, Action, StateContext, Selector, createSelector, NgxsOnInit } from '@ngxs/store';
import { SetActiveUnit, SetActiveUnitIfNotSet } from './unit.actions';

import { catchError, tap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import { AwareHttpService, IAwareCollection } from '@appbolaget/aware-http';
import { Unit } from '@appbolaget/aware-model';
import { AwareAuthService } from '@appbolaget/aware-auth';
import { Config } from '@services/config';

export interface UnitStateModel {
    active_unit: Unit;
    hierarchy: Unit[];
}

const defaultState: UnitStateModel = {
    active_unit: null,
    hierarchy: [],
};

@State<UnitStateModel>({
    name: 'unit',
    defaults: defaultState,
})
@Injectable()
export class UnitState implements NgxsOnInit {
    @Selector()
    static activeUnit({ active_unit }: UnitStateModel) {
        return active_unit;
    }

    @Selector()
    static children({ active_unit }: UnitStateModel) {
        return active_unit.children;
    }

    @Selector()
    static hierarchy({ hierarchy }: UnitStateModel): Unit[] {
        return hierarchy;
    }

    static childUnitsWithType(type: string) {
        return createSelector([UnitState], ({ active_unit }: UnitStateModel) => {
            return active_unit.children.filter((unit) => unit.type === type);
        });
    }

    private awareAuth = inject(AwareAuthService);
    private config = inject(Config);
    private api = inject(AwareHttpService);

    ngxsOnInit({ patchState }: StateContext<UnitStateModel>): void {
        this.awareAuth.onLogout$
            .pipe(
                tap(() => {
                    patchState(defaultState);

                    this.api.setDefaultHeader('unit', environment.api.unit);
                }),
            )
            .subscribe();
    }

    @Action(SetActiveUnit)
    setActiveUnit({ patchState, setState }: StateContext<UnitStateModel>, { unit }: SetActiveUnit) {
        if (!unit) {
            this.api.setDefaultHeader('unit', environment.api.unit);
            return setState(defaultState);
        }

        this.api.setDefaultHeader('unit', unit.uuid);

        return this.api
            .get<IAwareCollection<Unit>>('units/parents')
            .unit(unit.uuid)
            .toCollection(Unit)
            .execute()
            .pipe(
                tap((hierarchy) => {
                    this.config.setUnitConfigFromHierarchy(hierarchy.data);

                    patchState({
                        active_unit: hierarchy.data[0],
                        hierarchy: hierarchy.data,
                    });
                }),
            );
    }

    @Action(SetActiveUnitIfNotSet)
    setActiveUnitIfNotSet({ getState, dispatch }: StateContext<UnitStateModel>, { available_units, isGod }: SetActiveUnitIfNotSet) {
        const { active_unit } = getState();

        // If no active unit, set first from available
        if (!active_unit) {
            return dispatch(new SetActiveUnit(available_units[0]));
        }

        // New solution: Just use the parents request as a check if I can be here.
        return dispatch(new SetActiveUnit(active_unit)).pipe(catchError(() => dispatch(new SetActiveUnit(available_units[0]))));

        // Old solution: Check if my active unit exists in my available units.
        // If my active unit exists in my available units, set it.
        if (available_units.find((unit) => unit.uuid === active_unit.uuid) || isGod) {
            return dispatch(new SetActiveUnit(active_unit));
        }

        const checkIfParentExists = (unit: Unit) => {
            if (available_units.find((a) => a.uuid === unit.uuid)) {
                return true;
            }

            if (!unit.parent) {
                return false;
            }

            return checkIfParentExists(unit.parent);
        };

        const hasParentInAvailableUnits = checkIfParentExists(active_unit);

        if (hasParentInAvailableUnits) {
            return dispatch(new SetActiveUnit(active_unit));
        }

        return dispatch(new SetActiveUnit(available_units[0]));
    }
}
